const PII_PATTERNS: Array<[RegExp, string | ((s: string) => string)]> = [
  [/\/webhooks\/[^\s?#]+/g, '[SENSITIVE_API]'],
  [/\/internal\/[^\s?#]*/g, '[SENSITIVE_API]'],
  [/\/api\/v1\/auth\/(?:sign-in|verify-code|refresh|logout)/g, '[SENSITIVE_API]'],
  [/(^|[?&])((?:access[_-]?token|refresh[_-]?token|auth[_-]?token|token|secret|password|jwt|session|api[_-]?key|id[_-]?instance|idInstance)=)[^&#\s]+/gi, '$1$2[REDACTED]'],
  [/(\+?375|\+?[78])[\s()-]{0,2}\d{2,3}[\s()-]{0,2}\d{3}[\s-]{0,2}\d{2}[\s-]{0,2}\d{2}/g, '[PHONE]'],
  [/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL]'],
  [/Bearer\s+[A-Za-z0-9\-._~+/]+=*/g, 'Bearer [REDACTED]'],
  [/\b[a-f0-9]{32,}\b/g, '[SECRET]'],
  [/\b[A-Za-z0-9+/=]{40,}\b/g, (s: string): string => (s.includes('=') || s.length > 60 ? '[SECRET]' : s)],
]

const SENSITIVE_KEYS = /(?:authorization|cookie|password|secret|token|api[_-]?key|apikey|client[_-]?secret|webhook[_-]?secret|id[_-]?instance|idInstance|jwt|session)/i
const BODY_KEYS = /^(?:text|message|body|caption|content|payload)$/i
const REDACTED = '[REDACTED]'
const REDACTED_BODY = '[REDACTED_BODY]'
const SAFE_EXTRA_KEYS = new Set(['MICROFRONTEND_DSNS'])

const SENSITIVE_URL_PATTERNS = [
  /\/api\/v1\/auth\/(sign-in|verify-code|refresh|logout)/,
  /\/api\/v1\/channels\/[^/]+\/(qr[_-]login|init[_-]sms|verify[_-]sms|telegram\/(?:resend-code|email|verify-email))/,
  /\/internal\/[^?]*/,
  /\/webhooks\/[^?#]*/,
]

const SENSITIVE_HEADERS = new Set([
  'authorization',
  'cookie',
  'x-internal-token',
  'x-telegram-bot-api-secret-token',
  'x-max-bot-api-secret',
  'set-cookie',
])

function redactString(value: string): string {
  return PII_PATTERNS.reduce((result, [pattern, replacement]) => {
    return typeof replacement === 'function'
      ? result.replace(pattern, replacement)
      : result.replace(pattern, replacement)
  }, value)
}

function redactHeaders(headers: Record<string, unknown>): Record<string, unknown> {
  const redacted: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(headers)) {
    redacted[key] = SENSITIVE_HEADERS.has(key.toLowerCase()) || SENSITIVE_KEYS.test(key)
      ? REDACTED
      : typeof value === 'string'
        ? redactString(value)
        : value
  }
  return redacted
}

// Cookies целиком чувствительны: строка заменяется на REDACTED,
// в объекте редактируется каждое значение, а не только «чувствительные» ключи.
function redactCookies(cookies: Record<string, unknown>): Record<string, unknown> {
  const redacted: Record<string, unknown> = {}
  for (const key of Object.keys(cookies)) {
    redacted[key] = REDACTED
  }
  return redacted
}

function redactUrl(url: string): string {
  const redacted = redactString(url)
  for (const pattern of SENSITIVE_URL_PATTERNS) {
    if (pattern.test(redacted)) return redacted.replace(pattern, '[SENSITIVE_API]')
  }
  return redacted
}

function sanitizeValue(value: unknown, key?: string, seen = new WeakSet<object>()): unknown {
  if (key && SAFE_EXTRA_KEYS.has(key)) return value
  if (key && SENSITIVE_KEYS.test(key)) return REDACTED
  if (key && BODY_KEYS.test(key)) return REDACTED_BODY
  if (typeof value === 'string') return redactString(value)
  if (value === null || typeof value !== 'object') return value
  if (seen.has(value)) return '[Circular]'
  seen.add(value)

  if (Array.isArray(value)) return value.map((item) => sanitizeValue(item, undefined, seen))

  const result: Record<string, unknown> = {}
  for (const [childKey, childValue] of Object.entries(value as Record<string, unknown>)) {
    if (childKey === 'url' && typeof childValue === 'string') {
      result[childKey] = redactUrl(childValue)
    } else if (childKey === 'headers' && childValue && typeof childValue === 'object') {
      result[childKey] = redactHeaders(childValue as Record<string, unknown>)
    } else {
      result[childKey] = sanitizeValue(childValue, childKey, seen)
    }
  }
  return result
}

export function scrubEvent(event: Record<string, unknown>): Record<string, unknown> | null {
  if (!event) return event

  if (event.request && typeof event.request === 'object') {
    const request = event.request as Record<string, unknown>
    if (request.url) request.url = redactUrl(request.url as string)
    if (request.query_string) request.query_string = redactString(request.query_string as string)
    if (request.headers) request.headers = redactHeaders(request.headers as Record<string, unknown>)
    if (request.cookies) {
      request.cookies = typeof request.cookies === 'string'
        ? REDACTED
        : redactCookies(request.cookies as Record<string, unknown>)
    }
    if (request.data) request.data = REDACTED_BODY
    if (request.body) request.body = REDACTED_BODY
  }

  // Sentry.setUser({ email, ... }) раньше проходил мимо скраббера — редактируем
  if (event.user && typeof event.user === 'object') {
    event.user = sanitizeValue(event.user) as typeof event.user
  }

  if (typeof event.message === 'string') event.message = redactString(event.message)

  const exception = event.exception as Record<string, unknown> | undefined
  if (exception && Array.isArray(exception.values)) {
    for (const value of exception.values as Array<Record<string, unknown>>) {
      if (typeof value.value === 'string') value.value = redactString(value.value)
    }
  }

  if (event.extra && typeof event.extra === 'object') event.extra = sanitizeValue(event.extra) as Record<string, unknown>
  if (event.contexts && typeof event.contexts === 'object') event.contexts = sanitizeValue(event.contexts) as Record<string, unknown>
  if (Array.isArray(event.breadcrumbs)) event.breadcrumbs = event.breadcrumbs.map((item) => sanitizeValue(item))

  return event
}

export function scrubBreadcrumb(breadcrumb: Record<string, unknown>): Record<string, unknown> | null {
  if (!breadcrumb) return breadcrumb
  if (typeof breadcrumb.message === 'string') breadcrumb.message = redactString(breadcrumb.message)
  if (breadcrumb.data && typeof breadcrumb.data === 'object') {
    breadcrumb.data = sanitizeValue(breadcrumb.data) as Record<string, unknown>
  }
  return breadcrumb
}
